import {
  STATUS_ICON_BLOCKED,
  STATUS_ICON_CHECK_O, STATUS_ICON_DISABLED, STATUS_ICON_LOADING,
  STATUS_ICON_TIMES,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_MAINTENANCE,
  STATUS_ICON_WARNING,
  STATE_ICON_UNASSIGNED
} from '@/common/utils/statuses';
import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import { TaskState } from '@schema';

export const NODE_STATUSES = {
  AVAIL: 'avail',
  BEING_RELEASED: 'being_released',
  DROPPED: 'dropped',
  FAILED: 'failed',
  HEALTHY: 'healthy',
  OFFLINE: 'offline',
  RELEASING: 'releasing',
  UNASSIGNED: 'unassigned',
  MAINTENANCE: 'maintenance',
  WARNING: 'warning',
};

export const NODE_STATUSES_ICONS_MAP = {
  avail: STATUS_ICON_CHECK_O,
  dropped: STATUS_ICON_BLOCKED,
  healthy: STATUS_ICON_CHECK_O,
  being_released: STATUS_ICON_CHECK_O,
  unassigned: STATUS_ICON_TIMES,
  failed: STATUS_ICON_TIMES_O,
  offline: STATUS_ICON_DISABLED,
  releasing: STATUS_ICON_LOADING,
  maintenance: STATUS_ICON_MAINTENANCE,
  warning: STATUS_ICON_WARNING,
};

export const NODES_FILTER_TYPES = {
  STATUS: 'status',
  FAILURE_DOMAIN: 'failure-domain'
};

export enum NODES_STATUS_FILTERS {
  ALL_NODES = 'all-nodes',
  HEALTHY = 'healthy',
  UNHEALTHY = 'unhealthy',
  MAINTENANCE = 'maintenance',
  IN_PROGRESS = 'in-progress',
  UNASSIGNED = 'unassigned',
  RECOVERING = 'recovering',
  RECOVERY_FAILED = 'recovery_failed'
}

type NodesStatusFiltersTexts = Record<NODES_STATUS_FILTERS, string>

export const NODES_STATUS_FILTERS_TEXTS = computedLocale<NodesStatusFiltersTexts>(() => ({
  [NODES_STATUS_FILTERS.ALL_NODES]: i18n.t('All nodes'),
  [NODES_STATUS_FILTERS.HEALTHY]: i18n.t('Healthy'),
  [NODES_STATUS_FILTERS.UNHEALTHY]: i18n.t('Unhealthy'),
  [NODES_STATUS_FILTERS.MAINTENANCE]: i18n.t('Nodes in maintenance'),
  [NODES_STATUS_FILTERS.IN_PROGRESS]: i18n.t('In progress'),
  [NODES_STATUS_FILTERS.UNASSIGNED]: i18n.t('Unassigned'),
  [NODES_STATUS_FILTERS.RECOVERING]: i18n.t('Recovering'),
  [NODES_STATUS_FILTERS.RECOVERY_FAILED]: i18n.t('Recovery failed'),
  [NODE_STATUSES.WARNING]: i18n.t('Warning'),
}));

export const NODES_FAILURE_DOMAINS = {
  CLUSTER: 'cluster',
  ROOM: 'room',
  ROW: 'row',
  RACK: 'rack',
  HOST: 'host',
  DISK: 'disk'
};

export enum NODES_STATUSES_V3 {
  MAINTENANCE = 'maintenance',
  MAINTENANCE_SUSPENDING = 'maintenance suspending',
  MAINTENANCE_SUSPENDING_INCOMPLETE = 'maintenance suspending incomplete',
  MAINTENANCE_RESUMING = 'maintenance resuming',
  MAINTENANCE_RESUMING_INCOMPLETE = 'maintenance resuming incomplete',
  INSTALLING = 'installing',
  RELEASING = 'releasing',
  UNASSIGNED = 'unassigned',
  FAILED = 'failed',
  HEALTHY = 'healthy',
  IN_PROGRESS = 'in-progress',
  RECOVERING = 'recovering',
  RECOVERY_FAILED = 'recovery failed',
  WARNING = 'warning',
}

export type NODES_STATUSES_V3_TYPE = typeof NODES_STATUSES_V3[keyof typeof NODES_STATUSES_V3];

type NodesStatusMap = Record<NODES_STATUSES_V3, StatusData>;

export const NODES_STATUS_MAP = computedLocale<NodesStatusMap>(() => ({
  [NODES_STATUSES_V3.MAINTENANCE]: {
    icon: STATUS_ICON_MAINTENANCE,
    text: i18n.t('maintenance.status'),
  },
  [NODES_STATUSES_V3.MAINTENANCE_SUSPENDING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Entering maintenance'),
  },
  [NODES_STATUSES_V3.MAINTENANCE_SUSPENDING_INCOMPLETE]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Entering maintenance halted'),
  },
  [NODES_STATUSES_V3.MAINTENANCE_RESUMING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Exiting maintenance'),
  },
  [NODES_STATUSES_V3.MAINTENANCE_RESUMING_INCOMPLETE]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Exiting maintenance halted'),
  },
  [NODES_STATUSES_V3.INSTALLING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Installing'),
  },
  [NODES_STATUSES_V3.RELEASING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Releasing'),
  },
  [NODES_STATUSES_V3.UNASSIGNED]: {
    icon: STATE_ICON_UNASSIGNED,
    text: i18n.t('Unassigned'),
  },
  [NODES_STATUSES_V3.FAILED]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Failed'),
  },
  [NODES_STATUSES_V3.HEALTHY]: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Healthy'),
  },
  [NODES_STATUSES_V3.IN_PROGRESS]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('In progress'),
  },
  [NODES_STATUSES_V3.RECOVERING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Recovering'),
  },
  [NODES_STATUSES_V3.RECOVERY_FAILED]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Recovery failed'),
  },
  [NODES_STATUSES_V3.WARNING]: {
    icon: STATUS_ICON_WARNING,
    text: i18n.t('Warning'),
  },
}));

export const NODE_RAW_MAINTENANCE_STATE = {
  COMPLETED: 'completed',
  IDLE: 'idle',
  RESUMING: 'resuming',
  SUSPENDED_COMPLETE: 'suspended_complete',
  SUSPENDED_INCOMPLETE: 'suspended_incomplete',
  SUSPENDING: 'suspending',
};

export const NODE_RAW_MAINTENANCE_TASK_STATE = {
  FAILED: TaskState.FAILED,
  SUCCESS: TaskState.SUCCESS,
};

export const NODES_TAG_TYPE = {
  disabled: 'warning',
  maintenance: 'warning',
  releasing: 'info',
  joining: 'info',
  healthy: 'success',
  critical: 'danger',
  offline: 'inactive',
  warning: 'warning',
  reconfiguring: 'info',
  failed: 'danger',
};
